import { Database } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useDataCatalogOverview } from '../hooks/useDataCatalog.js';
import { toJstDateTime } from '../lib/data-catalog-format.js';
import type { SelectedNode, SelectedTab } from '../types/data-catalog.js';
import { CatalogSummary } from '../components/data-catalog/CatalogSummary.js';
import { FeatureTabs } from '../components/data-catalog/FeatureTabs.js';
import { TreePane } from '../components/data-catalog/TreePane.js';
import { DetailPane } from '../components/data-catalog/DetailPane.js';
import { FeatureDetailView } from '../components/data-catalog/FeatureDetailView.js';

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business データカタログページ。BQ + Firestore を統合ツリービューで表示し、機能別タブで絞り込み、詳細ペインでリネージ・カラム・メタデータを閲覧する。新テーブル/コレクション追加時に自動同期する。
 * @graph-connects useDataCatalogOverview [calls] データカタログ概要取得フック
 */
export function DataCatalogPage() {
  const { t } = useTranslation();
  const query = useDataCatalogOverview();
  const [selectedTab, setSelectedTab] = useState<SelectedTab>('all');
  const [selectedNode, setSelectedNode] = useState<SelectedNode>(null);

  const datasets = query.data?.bigquery.datasets ?? [];
  const collections = query.data?.firestore.collections ?? [];
  const featureMapping = query.data?.featureMapping ?? [];
  const bqTableCount = datasets.reduce((s, d) => s + d.tables.length, 0);
  const bqTotalSize = query.data?.bigquery.totalSizeBytes ?? 0;
  const fsTotalDocs = query.data?.firestore.totalDocs ?? 0;
  const selectedFeature =
    selectedTab !== 'all' ? featureMapping.find((fm) => fm.featureKey === selectedTab) : null;

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-8">
      <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
        Product
      </div>
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('dataCatalog.title')}
        </h1>
        {query.data?.fetchedAt && (
          <span className="text-xs text-[var(--color-text-muted)]">
            {t('common.lastUpdated')}: {toJstDateTime(query.data.fetchedAt)}
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
        {t('dataCatalog.description')}
      </p>

      {query.isLoading ? (
        <p className="mt-8 text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
      ) : query.isError ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('dataCatalog.fetchError')}
          </p>
        </Card>
      ) : datasets.length === 0 && collections.length === 0 ? (
        <Card className="mt-6 p-8 text-center">
          <Database className="mx-auto h-8 w-8 text-[var(--color-text-muted)]" aria-hidden="true" />
          <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
            {t('dataCatalog.noData')}
          </p>
        </Card>
      ) : (
        <>
          <div className="mt-4">
            <FeatureTabs
              featureMapping={featureMapping}
              selectedTab={selectedTab}
              onTabChange={(tab) => {
                setSelectedTab(tab);
                setSelectedNode(null);
              }}
            />
          </div>

          <div className="mt-4">
            <CatalogSummary
              bqDatasetCount={datasets.length}
              bqTableCount={bqTableCount}
              bqTotalSize={bqTotalSize}
              fsCollectionCount={collections.length}
              fsTotalDocs={fsTotalDocs}
            />
          </div>

          {selectedTab === 'all' ? (
            <div className="mt-4 flex gap-4">
              {/* Left pane: Tree — hidden on mobile, shown on lg+ */}
              <div
                className="hidden w-[340px] shrink-0 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 lg:block"
                style={{ maxHeight: 'calc(100vh - 320px)' }}
              >
                <TreePane
                  datasets={datasets}
                  collections={collections}
                  selectedNode={selectedNode}
                  onSelectNode={setSelectedNode}
                />
              </div>

              {/* Right pane: Detail — full width on mobile */}
              <div
                className="min-w-0 flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4"
                style={{ minHeight: '400px' }}
              >
                <DetailPane
                  selectedNode={selectedNode}
                  datasets={datasets}
                  collections={collections}
                  featureMapping={featureMapping}
                />
              </div>

              {/* Mobile: Tree below detail (stacked) */}
              <div
                className="fixed inset-x-0 bottom-0 z-10 block border-t border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 lg:hidden"
                style={{ maxHeight: '40vh', overflowY: 'auto' }}
              >
                <TreePane
                  datasets={datasets}
                  collections={collections}
                  selectedNode={selectedNode}
                  onSelectNode={setSelectedNode}
                />
              </div>
            </div>
          ) : (
            <div className="mt-4">
              {selectedFeature && (
                <FeatureDetailView
                  feature={selectedFeature}
                  datasets={datasets}
                  collections={collections}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
